/**
 * UPDATED FULL JAVASCRIPT DOCUMENT (DROP-IN REPLACEMENT)
 * -----------------------------------------------------
 * What changed vs your current file:
 * 1) Clean separation:
 *    - Pricing: computePriceBreakdown() (pure-ish, no validation side effects)
 *    - Fabric validation: validateFabricSelection()
 *    - UI errors: showFabricError(), clearFabricError()
 *    - ATC state: setAddToCartEnabled()
 *    - Orchestrator: refreshPricingAndValidation()
 *
 * 2) Fixes:
 *    - Price will not "grow" when switching fabrics (always recompute from base).
 *    - Errors won't stick (single error container, centralized clear/show).
 *    - Add to cart is disabled when fabric invalid (and hard-stopped on click).
 *    - Price display never permanently breaks ("—" removed). When invalid fabric, shows subtotal (base + options).
 *
 * 3) Keeps your existing behavior:
 *    - Your accordions toggle.
 *    - Your family filter (.Heading_container).
 *    - Your option click logic + notes toggling.
 *    - Your existing validations array for other required options.
 *    - Your addItemToCart() payload.
 *
 * IMPORTANT:
 * - This assumes fabric selection is done via: .fabric-swatch-grid .fabric-swatch.active[data-name="Color"]
 * - If you ALSO have a Fabric accordion option (data-meta="Fabric"), we still skip it in sumSelectedOptionPrices().
 */


/* =========================================
   DIMENSION INPUT LISTENER (unchanged, but routed to refresh)
========================================= */
document.querySelectorAll(".Inches_slector").forEach((input) => {
  input.addEventListener("input", () => {
    refreshPricingAndValidation({ source: 'dimension_input' });
  });
});


/* =========================================
   GENERAL HELPERS (kept + lightly cleaned)
========================================= */
function inchesToYards(inches) {
  return (parseFloat(inches) || 0) * 0.0278;
}

function inchesToMeters(inches) {
  return (parseFloat(inches) || 0) * 0.0254;
}

function metersToYards(meters) {
  return (parseFloat(meters) || 0) / 0.9144;
}

function metersToInches(meters) {
  return (parseFloat(meters) || 0) / 0.0254;
}

function roundUp(value, step) {
  step = step || 0.25; // 0.25 yd default
  return Math.ceil(value / step) * step;
}

function areaSqYards(width_inches, height_inches) {
  const w = inchesToYards(width_inches);
  const h = inchesToYards(height_inches);
  return w * h;
}

function parseMoneyText(txt) {
  const num = parseFloat(String(txt || '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(num) ? num : 0;
}

async function refreshCartDrawerSection() {
  try {
    const [htmlRes, cartRes] = await Promise.all([
      fetch('/?section_id=cart-drawer', {
        credentials: 'same-origin',
        cache: 'no-store'
      }),
      fetch('/cart.js', {
        credentials: 'same-origin',
        cache: 'no-store'
      })
    ]);

    const html = await htmlRes.text();
    const cart = await cartRes.json();

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const currentDrawer = document.querySelector('cart-drawer');
    const newDrawer = doc.querySelector('cart-drawer');

    const currentInner = currentDrawer?.querySelector('#CartDrawer');
    const newInner = newDrawer?.querySelector('#CartDrawer');

    if (!currentDrawer || !newDrawer || !currentInner || !newInner) {
      return false;
    }

    // Replace only the inner drawer markup
    currentInner.innerHTML = newInner.innerHTML;

    // Sync empty/non-empty state classes
    currentDrawer.classList.toggle('is-empty', (cart.item_count || 0) === 0);

    const currentDrawerInner = currentDrawer.querySelector('.drawer__inner');
    const newDrawerInner = newDrawer.querySelector('.drawer__inner');
    if (currentDrawerInner && newDrawerInner) {
      currentDrawerInner.className = newDrawerInner.className;
    }

    // Ensure the close button still works after replacement
    const closeBtn = currentDrawer.querySelector('.drawer__close');
    if (closeBtn) {
      closeBtn.onclick = function () {
        if (typeof currentDrawer.close === 'function') {
          currentDrawer.close();
        } else {
          currentDrawer.classList.remove('active');
          document.body.classList.remove('overflow-hidden');
        }
      };
    }

    return true;
  } catch (err) {
    console.error('refreshCartDrawerSection failed:', err);
    return false;
  }
}

async function refreshCartUIAndDrawer() {
  await refreshSampleCartUI();
  await refreshCartDrawerSection();
}

function isFabricSampleCartItem(item) {
  if (!item) return false;

  const hasSampleProperty =
    item.properties &&
    (
      item.properties['free-sample'] === 'free-sample' ||
      item.properties.free_sample === 'free-sample'
    );

  const titleStartsWithSample =
    typeof item.product_title === 'string' && item.product_title.indexOf('Fabric Sample -') === 0;

  return hasSampleProperty || titleStartsWithSample;
}

function getFabricSampleCount(cart) {
  if (!cart || !Array.isArray(cart.items)) return 0;

  return cart.items.reduce((total, item) => {
    if (isFabricSampleCartItem(item)) {
      return total + (parseInt(item.quantity, 10) || 0);
    }
    return total;
  }, 0);
}

async function canAddAnotherFabricSample(maxSamples = 10) {
  const response = await fetch('/cart.js', { credentials: 'same-origin' });
  const cart = await response.json();
  const sampleCount = getFabricSampleCount(cart);

  return {
    allowed: sampleCount < maxSamples,
    sampleCount,
    cart
  };
}

function showFabricSampleLimitMessage(maxSamples = 10) {
  alert(`You can only add up to ${maxSamples} fabric samples per order.`);
}

// Capture the ORIGINAL Shopify base price once (before we overwrite price text)
function captureBaseProductPriceOnce() {
  if (window.__BASE_PRODUCT_PRICE && window.__BASE_PRODUCT_PRICE > 0) return;

  // IMPORTANT: only read from the theme's base price element here.
  // Do NOT include span.Products_pricess if you overwrite it.
  const txt =
    $('.product__info-wrapper span.price-item.price-item--regular').first().text() ||
    $('span.price-item.price-item--regular').first().text() ||
    '';

  const base = parseMoneyText(txt);
  if (base > 0) window.__BASE_PRODUCT_PRICE = base;
}

function getBaseProductPrice(min_charge = 159) {
  captureBaseProductPriceOnce();
  return (window.__BASE_PRODUCT_PRICE && window.__BASE_PRODUCT_PRICE > 0)
    ? window.__BASE_PRODUCT_PRICE
    : min_charge;
}


/* =========================================
   FABRIC CALC + SELECTORS (pure helpers)
========================================= */
function calcFabricSqFtNoSeam({
  width_inches,
  height_inches,
  fabric_max_width_m,
  allowance_m = 0.25,      // same allowance you had before
  round_step = 0.25        // round up to nearest 0.25 sq ft (adjust if you want)
}) {
  const w_m = inchesToMeters(width_inches);
  const h_m = inchesToMeters(height_inches);

  const maxW = parseFloat(fabric_max_width_m) || 0;
  if (!maxW) {
    return { ok: false, reason: "missing_fabric_width", width_m: w_m, max_width_m: maxW };
  }

  if (w_m > maxW) {
    return {
      ok: false,
      reason: "shade_too_wide_for_fabric",
      width_m: w_m,
      max_width_m: maxW
    };
  }

  // Fabric “cut” length includes your allowance (same as old logic)
  const cutLen_m = Math.max(0, h_m + allowance_m);

  // Area of fabric used (no seam): roll-width * cut-length
  const area_m2 = w_m * cutLen_m;

  // Convert m² -> ft²
  const sqftRaw = area_m2 * 10.763910416709722;

  // Round up
  const sqft = roundUp(sqftRaw, round_step);

  return {
    ok: true,
    sqft,
    sqftRaw,
    area_m2,
    cutLen_m,
    width_m: w_m,
    height_m: h_m,
    max_width_m: maxW
  };
}

// Swatch-grid fabric selection (your current architecture)
function getSelectedFabricEl() {
  let $el = $('.fabric-swatch-grid .fabric-swatch.active[data-name="Color"]').first();
  if ($el.length) return $el;

  $el = $('.fabric-swatch-grid .fabric-swatch.active').first();
  if ($el.length) return $el;

  $el = $('[data-meta="Color_swatches"] .fabric-swatch.active').first();
  if ($el.length) return $el;

  $el = $('[data-meta="Color_swatches"] .products_wrapper.active').first();
  if ($el.length) return $el;

  return $();
}

function getSelectedFabric() {
  const el = getSelectedFabricEl();
  if (!el.length) return null;

  const read = (...keys) => {
    for (const key of keys) {
      const v = el.attr(key);
      if (v !== undefined && v !== null && v !== '') return v;
    }
    return '';
  };

  return {
    el,
    pricePerSqFt: parseFloat(read('data-price', 'price')) || 0,
    maxWidthM: parseFloat(read('data-max_width', 'data-max-width', 'max_width')) || 0,
    family: read('data-family', 'family') || '',
    colorName: read('data-color_name', 'data-color-name', 'color_name') || '',
    handle: read('data-fabric', 'data-handle', 'fabric') || ''
  };
}


/* =========================================
   OPTION PRICE SUM (skips fabric)
========================================= */
function sumSelectedOptionPrices() {
  let sum = 0;
  $('.product__accordions .popup_wrapper.active').each(function () {
    const meta = $(this).closest('[data-name]').attr('data-name') || '';
    if (meta.toLowerCase() === 'color') return; // skip fabric here
    sum += parseFloat($(this).attr('data-price')) || 0;
  });
  return sum;
}


/* =========================================
   UI: FABRIC ERROR (dynamic, reusable)
========================================= */
/* =========================================
   UI: FABRIC ERROR (match existing "Field_required" look + visible when accordion closed)
========================================= */
function ensureFabricErrorEl() {
  const $acc = $('[data-meta="Color_swatches"]').first(); // Fabric accordion wrapper
  if (!$acc.length) return null;

  // Use a dedicated ID like your other sections (Field_requiredX)
  // so it can be styled consistently and doesn't conflict.
  let $err = $acc.find('#Field_required_fabric').first();

  if (!$err.length) {
    const $title = $acc.find('.summary__title').first();
    if (!$title.length) return null;

    // Exact markup + classes from your Mount Type error block
    const errorHtml = `
      <div id="Field_required_fabric" class="Field_required_div rounded-md bg-red-50 p-3 mb-4 mt-1" style="display:none;">
        <div class="flex items-start">
          <div class="flex-shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" class="h-5 w-5 text-red-400">
              <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
            </svg>
          </div>
          <div class="ml-2">
            <div class="text-sm text-red-700 fabric-error-text">This feature is required</div>
          </div>
        </div>
      </div>
    `;

    // Insert immediately under the accordion title (so it shows even when closed)
    $title.after(errorHtml);
    $err = $acc.find('#Field_required_fabric').first();
  }

  return $err;
}

function showFabricError(message) {
  const $acc = $('[data-meta="Color_swatches"]').first();
  const $err = ensureFabricErrorEl();
  if (!$err) return;

  // Set the message in the same place your other error uses
  $err.find('.fabric-error-text').text(message || 'This feature is required');

  // Show it (no sliding needed; should be visible even when accordion is closed)
  $err.show();

  // Add "error" class to the accordion wrapper (if your theme uses this to style titles)
  $acc.addClass('error');

  // Mark state on ATC so click can hard-stop
  $('.custom_add_to_cart')
    .attr('data-fabric-invalid', '1')
    .attr('data-fabric-invalid-msg', message || 'Invalid fabric selection.');
}

function clearFabricError() {
  const $acc = $('[data-meta="Color_swatches"]').first();
  const $err = $acc.find('#Field_required_fabric').first();

  if ($err.length) $err.hide();

  // Remove error class
  $acc.removeClass('error');

  // Clear ATC invalid markers
  $('.custom_add_to_cart')
    .removeAttr('data-fabric-invalid')
    .removeAttr('data-fabric-invalid-msg');
}


function setAddToCartEnabled(enabled) {
  const $btn = $('.custom_add_to_cart');
  if (!$btn.length) return;

  if (enabled) {
    $btn.prop('disabled', false).removeClass('is-disabled');
    $btn.css({ pointerEvents: '', opacity: '' });
  } else {
    $btn.prop('disabled', true).addClass('is-disabled');
    $btn.css({ pointerEvents: 'none', opacity: '0.6' });
  }
}


/* =========================================
   VALIDATION: FABRIC (reusable)
========================================= */
function validateFabricSelection(width_inches) {
  const width_in = parseFloat(width_inches) || 0;
  const fabric = getSelectedFabric();

  if (!fabric) {
    return { valid: false, code: 'NO_FABRIC', message: 'Please select a fabric color.' };
  }

  if (!fabric.maxWidthM) {
    return {
      valid: false,
      code: 'MISSING_MAX_WIDTH',
      message: 'Selected fabric is missing a maximum width. Please set data-max_width on the swatch.'
    };
  }

  const maxWidthIn = metersToInches(fabric.maxWidthM);
  if (width_in > 0 && width_in > maxWidthIn) {
    return {
      valid: false,
      code: 'TOO_NARROW',
      message: `This fabric has a maximum width of ${maxWidthIn.toFixed(0)} inches. Your shade width is ${width_in.toFixed(0)} inches.`
    };
  }

  return { valid: true, code: 'OK', message: '' };
}


/* =========================================
   PRICING: deterministic recompute (no UI validation)
========================================= */
function computePriceBreakdown({
  width_inches,
  height_inches,
  min_charge = 159
}) {
  const w_in = parseFloat(width_inches) || 0;
  const h_in = parseFloat(height_inches) || 0;

  const baseProductPrice = getBaseProductPrice(min_charge);
  const options_total = sumSelectedOptionPrices();

  // NOTE: You had valance/bottom bar/control-type pricing in calculateShadePrice().
  // You ALSO store option prices in data-price and already click-activate them.
  // So we compute most add-ons via sumSelectedOptionPrices().
  // If you still have special "valance cost by width" logic, add it here (but only if valance isn't already in data-price).
  let total = baseProductPrice + options_total;

  // If dimensions missing, return base+options only
  if (w_in <= 0 || h_in <= 0) {
    return {
      baseProductPrice,
      options_total,
      fabric_cost: 0,
      fabric_calc: null,
      total
    };
  }

  const fabric = getSelectedFabric();
  if (!fabric) {
    // Fabric not selected => no fabric cost added, UI layer will error + disable
    return {
      baseProductPrice,
      options_total,
      fabric_cost: 0,
      fabric_calc: null,
      total
    };
  }

  const FABRIC_ALLOWANCE_M = 0.25;
  const SQFT_ROUND_STEP = 0.25;

  const fabric_calc = calcFabricSqFtNoSeam({
    width_inches: w_in,
    height_inches: h_in,
    fabric_max_width_m: fabric.maxWidthM,
    allowance_m: FABRIC_ALLOWANCE_M,
    round_step: SQFT_ROUND_STEP
  });

  if (!fabric_calc.ok) {
    // Invalid fabric dims -> do not add fabric cost
    return {
      baseProductPrice,
      options_total,
      fabric_cost: 0,
      fabric_calc,
      total
    };
  }

  const fabric_cost = (fabric_calc.sqft || 0) * (fabric.pricePerSqFt || 0);
  total += fabric_cost;

  return {
    baseProductPrice,
    options_total,
    fabric_cost,
    fabric_calc,
    total
  };
}


/* =========================================
   UI: render price (never "—")
========================================= */
function renderPrice(total) {
  const t = Number(total || 0);
  $('span.Products_pricess,.product__info-wrapper span.price-item.price-item--regular')
    .text(`$${t.toFixed(2)}`);
  $('.custom_add_to_cart').attr('data-price', t.toFixed(2));
}


/* =========================================
   ORCHESTRATOR: single source of truth for recalculation + validation + UI
========================================= */
function getDimensionValuesFromSelectors() {
  // Your selectors are .Inches_slector and .Fractions_slector
  // But pricing uses inches only (you pass values[0], values[1] previously).
  // In your URL restore you treated them as [w_in, w_frac, h_in, h_frac].
  // For live pricing, you currently only used Inches_slector (2 values).
  // We'll keep that behavior, but support Fractions_slector if present.

  const inches = Array.from(document.querySelectorAll(".Inches_slector")).map(el => parseFloat(el.value) || 0);
  const fracs  = Array.from(document.querySelectorAll(".Fractions_slector")).map(el => parseFloat(el.value) || 0);

  // If you have fractions, interpret as w = inches[0] + 1/fracs[0]? (unknown)
  // Your cart properties store "Width: inches/fraction" where fraction is denominator.
  // For pricing, safest is to use inches only (as your current live pricing does).
  return {
    width_inches: inches[0] || 0,
    height_inches: inches[1] || 0
  };
}

function refreshPricingAndValidation(opts = {}) {
  const { source, suppressErrors = false } = opts;
  // Pull current values from DOM
  const dims = getDimensionValuesFromSelectors();

  // Compute total deterministically
  const breakdown = computePriceBreakdown({
    width_inches: dims.width_inches,
    height_inches: dims.height_inches,
    min_charge: 0
  });

  // Fabric validation (required)
  const fabricValidation = validateFabricSelection(dims.width_inches);

  // If fabric invalid, show message + disable ATC
  if (!fabricValidation.valid) {
    if (!suppressErrors) {
      showFabricError(fabricValidation.message || 'Please select a fabric color');
    } else {
      clearFabricError();
    }
    setAddToCartEnabled(false);
  } else {
    // Fabric is selected and has max width; still guard if calc fails (should align)
    if (breakdown.fabric_calc && breakdown.fabric_calc.ok === false) {
      if (breakdown.fabric_calc.reason === "shade_too_wide_for_fabric") {
        const fabric = getSelectedFabric();
        const maxWidthIn = fabric ? metersToInches(fabric.maxWidthM) : 0;
        showFabricError(
          `This fabric has a maximum width of ${maxWidthIn.toFixed(0)} inches. Your shade width is ${Number(dims.width_inches).toFixed(0)} inches.`
        );
      } else {
        showFabricError('Selected fabric is missing a maximum width. Please set data-max_width on the swatch.');
      }
      setAddToCartEnabled(false);
    } else {
      clearFabricError();
      setAddToCartEnabled(true);
    }
  }

  // Price should never "break" — always show base+options subtotal (and fabric cost only if valid)
  renderPrice(breakdown.total);

  // Debug attrs (optional)
  $('.custom_add_to_cart')
    .attr('data-refresh-source', source || '')
    .attr('data-base-price', Number(breakdown.baseProductPrice || 0).toFixed(2))
    .attr('data-options-total', Number(breakdown.options_total || 0).toFixed(2))
    .attr('data-fabric-cost', Number(breakdown.fabric_cost || 0).toFixed(2));
}


/* =========================================
   KEEP your existing calculateShadePrice API
   (but now it simply routes to orchestrator)
========================================= */
function calculateShadePrice({
  width_inches,
  height_inches,
  valance_type,
  is_exposed_bottom_bar,
  is_motorized,
  min_charge = 159,
  is_price,
  suppressErrors = false,
  source = 'calculateShadePrice_call'
}) {
  // Keep compatibility: we allow your existing calls,
  // but we DO NOT do pricing incrementally here.
  // We store any per-width special pricing only if you truly need it.

  // If you still need valance_type etc, you can store globally
  // and use inside computePriceBreakdown() when required.
  window.__VALANCE_TYPE = valance_type || '';

  // If you had is_price passed previously, you can store it
  // and add it in computePriceBreakdown(). In this refactor,
  // we prefer sumSelectedOptionPrices(), so this is optional.
  // window.__IS_PRICE = parseFloat(is_price) || 0;

  // Always refresh from the orchestrator to avoid drift
  refreshPricingAndValidation({ source, suppressErrors });

  // Return current computed price number
  const p = parseFloat($('.custom_add_to_cart').attr('data-price')) || 0;
  return p;
}


/* =========================================
   ACCORDION TOGGLE (unchanged)
========================================= */
$('.product__accordions .summary__title').on('click', function () {
  const $currentAccordion = $(this).parent().find('.accordion__content');
  $('.product__accordions .accordion__content').not($currentAccordion).slideUp();
  $currentAccordion.slideToggle();
});


/* =========================================
   FAMILY FILTER (unchanged)
========================================= */
$('.Heading_container .color_name').on('click', function () {
  $(this).addClass('active').siblings().removeClass('active')
  let t = $(this).find('span').text().trim().toLowerCase();
  $('.fabric-swatch-grid .fabric-swatch').each(function(){
    let e = ($(this).attr('data-family') || '').trim().toLowerCase();
    if(e===t){$(this).show()}else{$(this).hide()}
  })
});


/* =========================================
   OPTION CLICK HANDLER
   - kept, but now routes to refreshPricingAndValidation()
========================================= */
$('.product__accordions .popup_wrapper').on('click', function () {
  if ($(this).hasClass('active')) {
    $(this).removeClass('active');
  } else {
    $(this).addClass('active').siblings().removeClass('active');
  }

  // Update notes UI (unchanged)
  let _data_val = $(this).attr('data')
  let has_class = $(this).hasClass('active')
  $(this).parents('.accordion__content').find('.properteis_notes .notes_loop').each(function () {
    if($(this).attr('data-match')===_data_val && has_class){
      $(this).slideToggle();
    }else{
      $(this).slideUp();
    }
  });

  // Refresh pricing + validation
  refreshPricingAndValidation({ source: 'option_click' });
});


/* =========================================
   FABRIC SWATCH CLICK
   - your HTML swatches are .fabric-swatch inside .fabric-swatch-grid
   - add active class here and refresh
========================================= */
$(document).on('click', '.fabric-swatch-grid .fabric-swatch', function (e) {
  // If clicking magnify button, don't change selection
  if ($(e.target).closest('.magnify-btn').length) return;

  $('.fabric-swatch-grid .fabric-swatch').removeClass('active');
  $(this).addClass('active');

  refreshPricingAndValidation({ source: 'fabric_click' });
});


/* =========================================
   DOM READY INIT (kept, but uses refresh)
========================================= */
document.addEventListener("DOMContentLoaded", function () {
  captureBaseProductPriceOnce();
});

document.addEventListener("DOMContentLoaded", function() {
  if($('.product__accordions').length){
    // Keep your initial call pattern
    calculateShadePrice({
      width_inches: 24,
      height_inches: 36,
      valance_type: '',
      is_exposed_bottom_bar: false,
      is_motorized: false,
      source: 'init',
      suppressErrors: true
    });
  }
});


/* =========================================
   ADD TO CART (unchanged)
========================================= */
function addItemToCart(id,price) {
  let items = [];
  let quantity = parseInt($('.custom-ATC input.quantity__input').val());
  let va = Array.from(document.querySelectorAll(".Inches_slector,.Fractions_slector")).map(el => parseFloat(el.value) || 0);
  let category_color = $('.color_name.active span').text()
  items.push({
    id: parseInt(id),
    quantity: 1,
    properties: {
      'Width': `${va[0]}/${va[1]}`,
      'Height': `${va[2]}/${va[3]}`,
      calculated_price: price,
      __actual_qty: quantity,
      __actual_price: price,
      __disabled: '__disabled',
    },
  });

  $('.product__accordions .products_wrapper.active').each(function (index) {
    let name = $(this).attr('data-name')
    let val =  $(this).attr('data')
    items[0].properties[name] = val
  })

  if(category_color) items[0].properties['Color_swatches'] = category_color

  let productData = { items: items };
  console.log("Adding Items to Cart:", productData);

  $.ajax({
    type: "POST",
    url: "/cart/add.js",
    data: JSON.stringify(productData),
    contentType: "application/json",
    dataType: "json",
    success: function () {
      window.location.href = '/cart';
      console.log("Item added to cart");
    },
    error: function (error) {
      console.error("Error adding item to cart:", error);
    },
  });
}


/* =========================================
   ADD TO CART CLICK VALIDATIONS
   - adds FABRIC validation as a FIRST-CLASS check
   - disables + scrolls + stops click when invalid
========================================= */
$(document).on('click', '.add_to_cart_button', async function (event) {
  event.preventDefault();

  const $btn = $(this);
  if ($btn.data('busy')) return;

  $btn.data('busy', true).text('Adding...');

  try {
    const sampleCheck = await canAddAnotherFabricSample(10);

    if (!sampleCheck.allowed) {
      showFabricSampleLimitMessage(10);
      $btn.data('busy', false).text('Free Sample');
      return;
    }
  } catch (err) {
    console.error('Failed to validate fabric sample limit:', err);
    $btn.data('busy', false).text('Free Sample');
    return;
  }

  const id = $btn.attr('data-id');

  const productData = {
    items: [{
      id: id,
      quantity: 1,
      properties: {
        'free-sample': 'free-sample'
      }
    }]
  };

  console.log('Items adding to cart:', productData.items);
  console.log('productData adding to cart:', productData);

  $.ajax({
    type: 'POST',
    url: '/cart/add.js',
    data: JSON.stringify(productData),
    contentType: 'application/json',
    dataType: 'json',
   success: async function () {
    try {
      await refreshSampleCartUI();
      await refreshCartDrawerSection();
      tryOpenCartDrawer();
    } catch (err) {
      console.error('cart refresh failed:', err);
    }

    $btn.data('busy', false);
  },
    error: function (xhr, status, error) {
      console.error('Add to cart failed');
      console.error('status:', xhr.status);
      console.error('statusText:', xhr.statusText);
      console.error('responseText:', xhr.responseText);
      console.error('status arg:', status);
      console.error('error arg:', error);
      console.error('productData:', productData);

      $btn.text('Free Sample').data('busy', false);
    }
  });
});

/* =========================================
   URL RESTORE / PRESELECT LOGIC (unchanged, but triggers refresh at end)
========================================= */
document.addEventListener("DOMContentLoaded", function () {
  if(window.location.pathname.includes('products')){
    let href = window.location.href.split('&');
    let Widthindex = href.findIndex(item => item.startsWith("Width"));
    let heightindex = href.findIndex(item => item.startsWith("Height"));
    let calculatedindex = href.findIndex(item => item.startsWith("calculated_price"));
    let qtyindex = href.findIndex(item => item.startsWith("quanity"));
    let Color_swatches = href.findIndex(item => item.startsWith("Color_swatches:"));
    let QTY = qtyindex !== -1 ? href[qtyindex].split(':')[1] : null;
    let calculated_price = calculatedindex !== -1 ? href[calculatedindex].split(':').pop() : null;
    let width_ = Widthindex !== -1 ? href[Widthindex].split(':').pop().split('/') : null;
    let height_ = heightindex !== -1 ? href[heightindex].split(':').pop().split('/') : null;
    let Color_swatches_value = Color_swatches !== -1 ? href[Color_swatches] : null;
    let values = [
      ...(Array.isArray(width_) ? width_ : []),
      ...(Array.isArray(height_) ? height_ : [])
    ];

    let selectors = document.querySelectorAll(".Inches_slector,.Fractions_slector");

    if(calculated_price){
      $('span.Products_pricess,.product__info-wrapper span.price-item.price-item--regular').text(`$${calculated_price}`)
      $('.custom_add_to_cart').attr('data-price', calculated_price);
    }
    if(QTY){
      $('.custom-ATC input.quantity__input').val(QTY)
    }
    if(Color_swatches_value){
      let Casette_sp = Color_swatches_value.split(':').pop().replaceAll('%20',' ')
      $(`.color_name[data-color="${Casette_sp}"]`).click()
    }else{
      $(`.Heading_container .color_name:first-child`).click()
    }

    if(selectors){
      selectors.forEach((selector, index) => {
        if (selector) {
          let optionExists = Array.from(selector.options).some(option => option.value === values[index]);
          if (optionExists) {
            selector.value = values[index];
          }
        }
      });
    }

    window.location.href.split('&').forEach(i => {
      if (i.includes(':')) {
        let filter =  i.split(':').pop().replaceAll('%20',' ')
        $('.products_wrapper.popup_wrapper').each(function () {
          if ($(this).attr('data') === filter) {
            $(this).addClass('active')
          }
        });
        $('.properteis_notes .notes_loop').each(function () {
          if($(this).attr('data-match')===filter){
            $(this).show();
          }
        });
      }
    });
  }

  $(`.free-fabric-swatches .Heading_container .color_name:first-child`).click();

  // IMPORTANT: refresh once everything is restored
  refreshPricingAndValidation({ source: 'url_restore' });
});


/* =========================================
   FREE SAMPLE CART HELPERS
========================================= */
function setHeaderCartCount(count) {
  const n = parseInt(count, 10) || 0;

  // Optional custom counter
  const $count = $('.site-header-cart--count');
  if ($count.length) {
    $count.attr('data-header-cart-count', n);
    $count.text(n);
  }

  // Exact Dawn cart badge
  const cartIcon = document.querySelector('#cart-icon-bubble');
  if (!cartIcon) return;

  let bubble = cartIcon.querySelector('.cart-count-bubble');

  if (n <= 0) {
    if (bubble) bubble.remove();
    return;
  }

  if (!bubble) {
    bubble = document.createElement('div');
    bubble.className = 'cart-count-bubble';
    bubble.innerHTML = `
      <span aria-hidden="true"></span>
      <span class="visually-hidden"></span>
    `;
    cartIcon.appendChild(bubble);
  }

  const visibleCount = bubble.querySelector('span[aria-hidden="true"]');
  const srCount = bubble.querySelector('.visually-hidden');

  if (visibleCount) visibleCount.textContent = String(n);
  if (srCount) srCount.textContent = `${n} item${n === 1 ? '' : 's'}`;
}

async function refreshSampleCartUI() {
  const response = await fetch('/cart.js', { credentials: 'same-origin' });
  const cart = await response.json();

  const items = Array.isArray(cart.items) ? cart.items : [];

  // Update your custom header/cart count
  setHeaderCartCount(cart.item_count || items.length || 0);

  // Reset all sample buttons first
  document.querySelectorAll('.add_to_cart_button').forEach(btn => btn.classList.remove('hidden'));
  document.querySelectorAll('.removeCartitem').forEach(btn => btn.classList.add('hidden'));

  // Show "Added" state for items currently in cart
  items.forEach(item => {
    document.querySelectorAll(`.add_to_cart_button[data-id="${item.id}"]`).forEach(btn => {
      btn.classList.add('hidden');
    });

    document.querySelectorAll(`.removeCartitem[data-id="${item.id}"]`).forEach(btn => {
      btn.classList.remove('hidden');
      btn.innerHTML = svgtext; // restore Added state
    });
  });

  return cart;
}

function tryOpenCartDrawer() {
  // 1) Dawn cart-drawer custom element
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    try {
      if (typeof cartDrawer.open === 'function') {
        cartDrawer.open();
        return true;
      }
      cartDrawer.classList.add('active');
      document.body.classList.add('overflow-hidden');
      return true;
    } catch (e) {
      console.error('cartDrawer.open failed:', e);
    }
  }

  // 2) Try clicking common cart icon / cart drawer trigger
  const selectors = [
    'a[href="/cart"]',
    'a[href$="/cart"]',
    '[href="/cart"]',
    '[data-cart-trigger]',
    '[aria-controls="CartDrawer"]',
    '.header__icon--cart',
    '.site-header__cart',
    '.cart-icon-bubble'
  ];

  for (const selector of selectors) {
    const el = document.querySelector(selector);
    if (el) {
      el.click();
      return true;
    }
  }

  return false;
}


/* =========================================
   FREE SAMPLE REMOVE
========================================= */
$(document).on('click', '.removeCartitem', function (event) {
  event.preventDefault();

  const $btn = $(this);
  if ($btn.data('busy')) return;

  $btn.data('busy', true).text('Removing...');

  const variantId = $btn.attr('data-id');

  $.ajax({
    type: 'POST',
    url: '/cart/change.js',
    // keep this in the original simple form style
    data: {
      quantity: 0,
      id: variantId
    },
    dataType: 'json',
    success: async function () {
      try {
        await refreshSampleCartUI();
        await refreshCartDrawerSection();
      } catch (err) {
        console.error('refreshSampleCartUI failed after remove:', err);
      }

      $btn.data('busy', false);
    },
    error: function (xhr, status, error) {
      console.error('Error removing linked products:', error);
      console.error('status:', xhr.status);
      console.error('responseText:', xhr.responseText);

      $btn.html(svgtext).data('busy', false);
    }
  });
});


/* =========================================
   CART ITEMS CHECK
========================================= */
async function gettheitems() {
  await refreshSampleCartUI();
}

document.addEventListener('DOMContentLoaded', function () {
  refreshSampleCartUI();
});

/* =========================================
   HOVERS (unchanged)
========================================= */
let svgtext = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" class="w-4 h-4 mr-0.5" stroke-width="5"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path></svg><span>Added</span>'
$('.removeCartitem').hover(
  function() { $(this).text('Remove'); },
  function() { $(this).html(svgtext); }
);
$('.add_to_cart_button').hover(
  function() { $(this).text('Add To Cart'); },
  function() { $(this).text('Free Sample'); }
);


/* =========================================
   CUSTOM CHECKOUT (unchanged)
========================================= */
$('.custom_checkout').click(function(){
  event.preventDefault();
  $(this).css('pointer-events', 'none')
  $(this).find('svg').show()
  console.log('here');
  $.getJSON("/cart.json", function(cartData) {
    console.log("Shopify Cart Data:", cartData);
    $.ajax({
      url: "https://royal-silence-00ce.misty-fire-270a.workers.dev/",
      type: "POST",
      data: JSON.stringify(cartData),
      contentType: "application/json",
      success: function(response) {
        console.log("Data sent successfully:", response);
        $(this).find('svg').hide()
        window.responsee = response;
        setTimeout(function(){window.location.href = window.responsee.draft_order.invoice_url;},500)
      },
      error: function(xhr, status, error) {
        console.error("Error sending data:", error);
      }
    });
  }).fail(function() {
    console.error("Failed to fetch Shopify cart data.");
  });
});


$(document).on('click', '.custom_add_to_cart', function (e) {
  e.preventDefault();

  const $btn = $(this);

  const id = $btn.attr('data-id');
  const price = $btn.attr('data-price');

  console.log('Adding roller shade to cart', id);

  addItemToCart(id, price);
});